import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import MarkdownRender from 'LuxKit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import PropertyReview from './PropertyReview'
import LayoutContainer from 'components/Common/LayoutContainer'
import ThingsToKnow from '../ThingsToKnow/ThingsToKnow'
import TraderInformation from './TraderInformation'
import BodyText from 'LuxKit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import { isBundleOffer, isFlashHotelOffer, isLEHotel } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import UserReviewSection from './UserReviewSection'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferLocationMap from 'components/OfferPage/NavigableOfferContent/OfferLocationMap'
import OfferPropertySurroundingsHideExperiment from 'components/OfferPage/NavigableOfferContent/OfferPropertySurroundingsHideExperiment'
import PropertyStructuredFacilities from '../Property/PropertyStructuredFacilities'
import { hasMapCoordinates } from './mapCoordinates'
import OpenInGoogleMapsButton, { LocationAddressRow } from './OpenInGoogleMapsButton'
import useNavigableOfferContentScroll, {
  NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
} from './useNavigableOfferContentScroll'
import StructuredFacilitiesReworkActivation, {
  StructuredFacilitiesReworkStandaloneActivation,
} from './StructuredFacilitiesReworkActivation'
import useStructuredFacilitiesRework, {
  buildStructuredFacilitiesActivationPlacement,
  type StructuredFacilitiesActivationPlacement,
} from './useStructuredFacilitiesRework'

import { maybeTrackReadMoreClientEvent } from './analytics'
import { OfferPageSnapshotContributionRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { buildOfferPageAccordionContribution } from 'tripPlanner/assistant/surfaces/offerPage/offerPageAccordionContribution'

interface Props {
  hash?: string
  offerDetails: App.Offer
  traderInformation?: App.TraderInformation
  mobileHotelOfferPageTest?: boolean
  charterBundleRoute?: App.CharterBundleRoute
}

const StyledFacilities = styled(MarkdownRender)`
  > ul {
    columns: auto 2;
  }
`

function packageHasPropertyWithCoords(offer: App.Offer) {
  if (isBundleOffer(offer)) {
    return Object.values(offer.bundledOffers ?? {}).some(bundleOffer => hasMapCoordinates(bundleOffer.property))
  }

  return hasMapCoordinates(offer.property)
}

interface ContentField {
  id: string
  label: string
  open: boolean
  collapsable: boolean
  initialHeight?: number
  minimumContentHeight?: number
  unconstrainedWhenExpanded?: boolean
  contentComponent: React.ReactElement | Array<React.ReactElement>
}

interface ContentFieldsOptions {
  shouldHideHighlights: boolean
  intl: ReturnType<typeof useIntl>
  structuredFacilitiesReworkActivationRef: React.RefCallback<HTMLDivElement>
  structuredFacilitiesRework?: Array<App.StructuredFacilityGroup>
  shouldTrackFacilitiesExperimentInView: boolean
}

export function shouldHideHighlightsForOffer(
  offerDetails: App.Offer,
  previewMode: boolean,
  charterBundleRoute?: App.CharterBundleRoute,
) {
  const hasHighlights = !!offerDetails.highlights
  const isFlashHotel = isFlashHotelOffer(offerDetails)
  const isMaldivesOffer = offerDetails.property?.geoData.countryCode?.toLowerCase() === 'mv'
  const shouldShowMaldivesHighlights = isMaldivesOffer || !!charterBundleRoute

  return isFlashHotel && hasHighlights && !previewMode && !shouldShowMaldivesHighlights
}

function getContentFields(props: Props, options: ContentFieldsOptions): {
  contentFields: Array<ContentField>
  structuredFacilitiesActivationPlacement?: StructuredFacilitiesActivationPlacement
} {
  const {
    hash,
    offerDetails,
    mobileHotelOfferPageTest,
  } = props

  const {
    shouldHideHighlights,
    intl,
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework,
    shouldTrackFacilitiesExperimentInView,
  } = options

  const {
    highlights,
    facilities,
    finePrint,
    gettingThere,
    lowestPricePackage,
    property,
  } = offerDetails

  const contentFields: Array<ContentField> = []
  const offerLocationMapOffer = (isLEHotel(offerDetails) || isBundleOffer(offerDetails)) && packageHasPropertyWithCoords(offerDetails) ?
    offerDetails :
    undefined

  if (offerLocationMapOffer) {
    const bundledOffers = Object.values(offerDetails.bundledOffers ?? {})

    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'uxQ6Lpw1yvGNGNrXgtqe',
        defaultMessage: 'Location',
        description: 'Section label for the property location information on the offer detail page',
      }),
      open: hash === '#getting-there',
      collapsable: false,
      contentComponent: <Group gap={20} direction="vertical">
        <OfferLocationMap
          offer={offerLocationMapOffer}
          height={258}
        />
        {isBundleOffer(offerDetails) && <Group gap={16} direction="vertical">
          <div>
            <BodyText variant="medium">{bundledOffers[0].property?.name}</BodyText>
            <LocationAddressRow>
              <BodyText variant="medium" weight="bold">{bundledOffers[0].property?.address}</BodyText>
              {hasMapCoordinates(bundledOffers[0].property) && <OpenInGoogleMapsButton
                query={bundledOffers[0].property.address}
                placeId={bundledOffers[0].property.geoData?.placeId}
              />}
            </LocationAddressRow>
          </div>
          <div>
            <BodyText variant="medium">{bundledOffers[1].property?.name}</BodyText>
            <LocationAddressRow>
              <BodyText variant="medium" weight="bold">{bundledOffers[1].property?.address}</BodyText>
              {hasMapCoordinates(bundledOffers[1].property) && <OpenInGoogleMapsButton
                query={bundledOffers[1].property.address}
                placeId={bundledOffers[1].property.geoData?.placeId}
              />}
            </LocationAddressRow>
          </div>
        </Group>}
        {!isBundleOffer(offerDetails) && <div>
          <BodyText variant="medium">{property?.name}</BodyText>
          <LocationAddressRow>
            <BodyText variant="medium" weight="bold">{property?.address}</BodyText>
            {hasMapCoordinates(property) && <OpenInGoogleMapsButton
              query={property.address}
              placeId={property.geoData?.placeId}
            />}
          </LocationAddressRow>
        </div>}
        {isFlashHotelOffer(offerDetails) && offerDetails.id && property && hasMapCoordinates(property) && (
          <OfferPropertySurroundingsHideExperiment
            offerId={offerDetails.id}
            latitude={property.latitude}
            longitude={property.longitude}
          />
        )}
      </Group>,
    })
  }

  if (highlights && !mobileHotelOfferPageTest && !shouldHideHighlights) {
    contentFields.push({
      id: 'highlights',
      label: intl.formatMessage({
        id: 'qaSBdSwGBWHvz9Fn04C_',
        defaultMessage: 'Highlights',
        description: 'Section label for the property or offer highlights on the offer detail page',
      }),
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={highlights} />,
      open: hash === '#highlights',
    })
  }

  const facilitiesSectionInsertIndex = contentFields.length
  let mountedFacilitiesExperimentActivation = false

  if (structuredFacilitiesRework || facilities) {
    if (shouldTrackFacilitiesExperimentInView) {
      mountedFacilitiesExperimentActivation = true
    }

    contentFields.push({
      id: 'facilities',
      label: intl.formatMessage({
        id: 'dOZd09w4GyaQT9Hx_3T2',
        defaultMessage: 'Facilities',
        description: 'Section label for the property facilities and amenities on the offer detail page',
      }),
      open: hash === '#facilities',
      collapsable: true,
      initialHeight: structuredFacilitiesRework ? 240 : 120,
      unconstrainedWhenExpanded: !!structuredFacilitiesRework,
      contentComponent: shouldTrackFacilitiesExperimentInView ? <StructuredFacilitiesReworkActivation
        activationRef={structuredFacilitiesReworkActivationRef}
      >
        {structuredFacilitiesRework ? <PropertyStructuredFacilities
          showHeading={false}
          structuredFacilities={structuredFacilitiesRework}
        /> : <StyledFacilities type="compact" fontSize="medium" content={facilities} />}
      </StructuredFacilitiesReworkActivation> : <StyledFacilities type="compact" fontSize="medium" content={facilities} />,
    })
  }

  if (gettingThere && !packageHasPropertyWithCoords(offerDetails)) {
    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'uxQ6Lpw1yvGNGNrXgtqe',
        defaultMessage: 'Location',
        description: 'Section label for the property location information on the offer detail page',
      }),
      open: hash === '#getting-there',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={gettingThere} />,
    })
  }

  if (lowestPricePackage?.tour?.itinerary) {
    contentFields.push({
      id: 'itinerary',
      label: intl.formatMessage({
        id: '5iB3ZwoAt2AVjtd0s5D1',
        defaultMessage: 'Itinerary',
        description: 'Navigation tab label for the tour itinerary section on the offer page',
      }),
      open: hash === '#itinerary',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={lowestPricePackage.tour.itinerary} />,
    })
  }

  if (finePrint || offerDetails.inclusions || offerDetails.facilities) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: '9WsTECHeqsvJ8UhB4MQQ',
        defaultMessage: 'Things to know',
        description: 'Form field label for things to know section on the offer detail page',
      }),
      open: !!offerDetails.property?.taxesAndFeesContent || hash === '#fine-print',
      collapsable: false,
      contentComponent: <ThingsToKnow offer={offerDetails} />,
    })
  }

  if (lowestPricePackage?.tour?.reviews.length) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'TZJz7mXS-hBKgRPgoX1a',
        defaultMessage: 'Reviews',
        description: 'Navigation tab label for the guest reviews section on the hotel offer detail page',
      }),
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: lowestPricePackage.tour.reviews.map(review =>
        <PropertyReview
          key={review.id}
          content={review.content}
          source={review.source}
        />,
      ),
    })
  }

  if (!isBundleOffer(offerDetails) && property?.rating?.reviewsTotal && property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'TZJz7mXS-hBKgRPgoX1a',
        defaultMessage: 'Reviews',
        description: 'Navigation tab label for the guest reviews section on the hotel offer detail page',
      }),
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: <UserReviewSection property={property} />,
    })
  }

  return {
    contentFields,
    structuredFacilitiesActivationPlacement: buildStructuredFacilitiesActivationPlacement({
      shouldTrackFacilitiesExperimentInView,
      mountedFacilitiesExperimentActivation,
      facilitiesSectionInsertIndex,
    }),
  }
}

function NavigableOfferContent(props: Props) {
  const intl = useIntl()
  const previewMode = useAppSelector(state => state.system.previewMode)

  const shouldHideHighlights = shouldHideHighlightsForOffer(props.offerDetails, previewMode, props.charterBundleRoute)
  const {
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework,
    shouldTrackFacilitiesExperimentInView,
  } = useStructuredFacilitiesRework(props.offerDetails)

  const { hash, offerDetails, mobileHotelOfferPageTest } = props
  const {
    contentFields,
    structuredFacilitiesActivationPlacement,
  } = useMemo(
    () => getContentFields(props, {
      shouldHideHighlights,
      intl,
      structuredFacilitiesReworkActivationRef,
      structuredFacilitiesRework,
      shouldTrackFacilitiesExperimentInView,
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- destructured props fields used as stable deps
    [
      hash,
      offerDetails,
      mobileHotelOfferPageTest,
      shouldHideHighlights,
      intl,
      structuredFacilitiesReworkActivationRef,
      structuredFacilitiesRework,
      shouldTrackFacilitiesExperimentInView,
    ],
  )
  const defaultOpenField = contentFields.find(field => field.open)

  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)
  const { contentGroupRefs, scrollToContentGroup } = useNavigableOfferContentScroll(
    NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
  )
  const accordionContribution = useMemo(
    () => buildOfferPageAccordionContribution('navigable', headerSelected),
    [headerSelected],
  )

  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
    }
  }, [])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToContentGroup(id)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields, scrollToContentGroup])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    maybeTrackReadMoreClientEvent(id, !openFields[id])
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const showModal = useContext(ModalContext)

  const onModalOpenToggle = useCallback(() => {
    showModal(<Modal
      title={intl.formatMessage({
        id: 'jUzSkjpRpxP8dBlhmcsC',
        defaultMessage: 'About this supplier',
        description: 'Heading title for supplier information modal on the offer detail page',
      })}
    >
      <TraderInformation traderInformation={props.traderInformation!} />
    </Modal>)
  }, [showModal, props.traderInformation, intl])

  return (
    <>
      <OfferPageSnapshotContributionRegistration contribution={accordionContribution} />
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <LayoutContainer variant="large">
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {structuredFacilitiesActivationPlacement?.insertIndex === index && (
                <StructuredFacilitiesReworkStandaloneActivation
                  activationRef={structuredFacilitiesReworkActivationRef}
                />
              )}
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                ref={(node) => { contentGroupRefs.current[contentField.id] = node }}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                unconstrainedWhenExpanded={contentField.unconstrainedWhenExpanded}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
                collapsable={contentField.collapsable}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {structuredFacilitiesActivationPlacement?.insertIndex === contentFields.length && (
              <StructuredFacilitiesReworkStandaloneActivation
                activationRef={structuredFacilitiesReworkActivationRef}
              />
            )}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={onModalOpenToggle}>
                  {intl.formatMessage({
                    id: 'c9vgdmdbjOkQDIzx2cF_',
                    defaultMessage: 'About this supplier',
                    description: 'Hyperlink text to view supplier information on the offer detail page',
                  })}
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default NavigableOfferContent
